/**
 * Avatar cache — last-confirmed-good avatar URL, persisted per user.
 *
 * The point: when the UI loads right after login, we render the avatar that
 * was *confirmed to load* last time, and only replace it once a new candidate
 * URL has actually loaded (see CachedAvatar). A failed/404 avatar never
 * overwrites a known-good one, and the cache is scoped per user so a different
 * login never inherits the previous user's avatar.
 *
 * Stored in IndexedDB via idb-keyval (same store the React Query persister
 * uses; distinct key namespace). All ops are best-effort — a storage failure
 * degrades to "no cache", never throws into render.
 */
import { get, set, del } from 'idb-keyval';

const keyFor = (userId: string) => `fb-avatar:${userId}`;

export async function getCachedAvatar(userId: string): Promise<string | null> {
  try {
    const v = await get<string>(keyFor(userId));
    return v ?? null;
  } catch {
    return null;
  }
}

export async function setCachedAvatar(userId: string, url: string): Promise<void> {
  try {
    await set(keyFor(userId), url);
  } catch {
    /* best-effort */
  }
}

export async function clearCachedAvatar(userId: string): Promise<void> {
  try {
    await del(keyFor(userId));
  } catch {
    /* best-effort */
  }
}
